import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { useFeatureFlagEnabled } from '@posthog/react'

import { isAuthenticated } from '~/auth/slices/auth.slice'
import { useAppSelector } from '~/redux/hooks'
import { DefaultLayout } from '~/shared/layouts'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'
import HomMethods from '~/onboarding/components/HomMethods'
import { selectShowHomMethods } from '~/onboarding/redux/onboarding.slice'
import { DashboardProps } from '~/dashboard/types/dashboard.types'
import { isCertificateClaimingApplicable, isMappingQuizApplicable } from '~/curriculum/utils/validateCountryCodeGrade'
import { CurriculumCountryCodes } from '~/curriculum/constants/curriculum.const'
import { getTestGenerationStatus } from '~/tests/utils/testUtils'
import appConfig from '~/config/app'

import { Link } from '~/components/Link'
import ROUTES from '~/shared/constants/routes.const'
import UpgradeBanner from '~/components/UpgradeBanner'
import { useHasPremiumAccess } from '~/redux/utils/subscription'
import useCurrentFamily from '~/familyAccount/hooks/useCurrentFamily'
import useCurrentUser from '~/hooks/useCurrentUser'
import HouseOfMathLoader from '~/components/HouseOfMathLoader'

import { SubscriptionModalV1 } from '~/components/Modals/SubscriptionModalV1'
import useSubscriptionIsRequired from '~/subscription/hooks/useSubscriptionIsRequired'

import WeeklyChart from '../components/WeeklyChart'
import LeagueContainer from '../components/LeagueContainer'
import DashboardGamificationCard from '../components/DashboardGamificationCard'
import WeeklyStats from '../components/WeeklyStats'
import MasteryTrackPanel from '../components/MasteryTrackPanel'
import CertificateAndTestPrep from '../components/CertificateAndTestPrep'
import DashboardStreakCard from '../components/DashboardStreakCard'

export default function Dashboard(props: DashboardProps): React.ReactElement {
  const { t } = useTranslation(['registerAccount', 'dashboard'])
  const authenticated = useAppSelector(isAuthenticated)
  const router = useRouter()
  const { isSubscriptionRequired } = useSubscriptionIsRequired()
  const showDashboardStats = !!useFeatureFlagEnabled('dashboard-stats')

  const masteryDesktopRef = useRef<HTMLDivElement>(null)
  const masteryMobileRef = useRef<HTMLDivElement>(null)

  const countryCode = useGetUserChosenCurriculumCountryCode()
  const { data } = useGetCurrentGradeQuery(undefined, { skip: !authenticated })

  const showHomMethodsPopup = useAppSelector(selectShowHomMethods)
  const isMappingQuizAvailable = isMappingQuizApplicable({ countryCode, grade: `${data?.grade ?? 0}` })
  const showHomMethods = showHomMethodsPopup && isMappingQuizAvailable && Boolean(props.homMethodsData)
  const hasFullAccess = useHasPremiumAccess()
  const shouldHighlightMastery =
    router.isReady &&
    (Array.isArray(router.query.highlight)
      ? router.query.highlight.includes('mastery')
      : router.query.highlight === 'mastery')

  const user = useCurrentUser()
  const { isFamilyAdmin: isFamilyAdminFromProfiles, hasLicense: hasLicenseFromProfiles } = useCurrentFamily()
  // /me is already loaded when the paywall renders; the family query may still be refetching
  const isFamilyAdmin = isFamilyAdminFromProfiles || Boolean(user?.familyInfo?.isFamilyAdmin)
  const hasLicense = hasLicenseFromProfiles || Boolean(user?.familyInfo?.hasLicense)

  const showTestPrep = !getTestGenerationStatus(data?.grade ?? 0, countryCode as unknown as CurriculumCountryCodes)
    .isNotAllowed

  // /family/switch-profile 404s in the school platform, so a licence-less family admin stays on the
  // dashboard rather than being bounced into a dead route.
  const isLeavingForSwitchProfile = isFamilyAdmin && !hasLicense && !appConfig.isSchoolPlatform

  useEffect(() => {
    if (isLeavingForSwitchProfile) {
      router.replace(ROUTES.FAMILY_SWITCH_PROFILE)
    }
  }, [isLeavingForSwitchProfile, router])

  useEffect(() => {
    if (!authenticated || !shouldHighlightMastery) return

    const stripHighlightQueryFromHistory = (): void => {
      const queryWithoutHighlight = { ...router.query }
      delete queryWithoutHighlight.highlight
      void router.replace({ pathname: router.pathname, query: queryWithoutHighlight }, undefined, { shallow: true })
    }

    const startAnimation = window.setTimeout(() => {
      const isDesktopVisible = masteryDesktopRef.current?.offsetParent !== null
      const isMobileVisible = masteryMobileRef.current?.offsetParent !== null
      const visibleTarget = isDesktopVisible ? masteryDesktopRef.current : masteryMobileRef.current
      if (!visibleTarget) {
        stripHighlightQueryFromHistory()
        return
      }

      if (isMobileVisible) {
        visibleTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      const animation = visibleTarget.animate(
        [
          { transform: 'scale(1)', opacity: 1 },
          { transform: 'scale(1.04)', opacity: 0.78 },
          { transform: 'scale(1)', opacity: 1 },
        ],
        { duration: 650, easing: 'ease-in-out', iterations: 5 },
      )

      void animation.finished
        .then(() => {
          stripHighlightQueryFromHistory()
        })
        .catch(() => {
          // Animation may be cancelled (e.g. navigate away); still clean URL if highlight present
          stripHighlightQueryFromHistory()
        })
    }, 1000)

    return () => {
      window.clearTimeout(startAnimation)
    }
  }, [authenticated, shouldHighlightMastery, router])

  // Don't paint a dashboard this user is about to be redirected off
  if (isLeavingForSwitchProfile) {
    return (
      <DefaultLayout dashboardGradientClass="bg-gradient-avatar-frame" zeroPadding>
        <div className="flex justify-center w-full">
          <HouseOfMathLoader className="flex bg-white rounded p-2 max-w-66 mt-20" />
        </div>
      </DefaultLayout>
    )
  }

  return (
    <>
      {showHomMethods && <HomMethods data={props.homMethodsData} />}
      <DefaultLayout dashboardGradientClass="bg-gradient-avatar-frame" disableScroll={showHomMethods} zeroPadding>
        <div className="w-full">
          {authenticated ? (
            <div className="flex flex-col justify-center items-center mx-2 px-2 md:px-0 xl:px-6">
              {!hasFullAccess && !appConfig.isMobileApp && <UpgradeBanner />}

              <div
                className={`flex flex-col lg:gap-4 justify-center w-full max-w-85 md:max-w-full md:w-147 lg:w-full lg:max-w-[1060px] text-white z-20 ${
                  hasFullAccess ? 'lg:my-20' : 'my-5'
                }`}
              >
                <div className="hidden lg:grid grid-cols-4 w-full">
                  <div className="flex flex-col gap-4 col-span-1">
                    <div className="flex items-center bg-purple-cosmos border border-white-veil rounded-4 h-80">
                      <DashboardGamificationCard />
                    </div>
                    <div className="flex justify-center bg-purple-cosmos border border-white-veil rounded-4 h-80">
                      <LeagueContainer />
                    </div>
                  </div>

                  <div className="flex flex-col justify-start items-center gap-4 col-span-2 mt-5">
                    <div className="gap-4 grid grid-cols-1 px-6 w-full">
                      <MasteryTrackPanel masteryTrackPanelRef={masteryDesktopRef} />

                      <div className="flex justify-center items-center h-65">
                        <WeeklyChart />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4 col-span-1">
                    <div className={showTestPrep ? 'h-52' : 'h-80'}>
                      <DashboardStreakCard />
                    </div>
                    {isCertificateClaimingApplicable({ countryCode }) && (
                      <div className={showTestPrep ? 'h-108' : 'h-80'}>
                        <CertificateAndTestPrep
                          countryCode={countryCode as unknown as CurriculumCountryCodes}
                          currentGrade={data?.grade ?? 0}
                        />
                      </div>
                    )}
                    {showDashboardStats && (
                      <div>
                        <WeeklyStats />
                      </div>
                    )}
                  </div>
                </div>

                <div className="lg:hidden gap-4 grid mx-auto my-2 w-full max-w-150">
                  <div className="flex justify-center items-center bg-purple-cosmos border border-white-veil rounded-4 w-full h-80">
                    <DashboardGamificationCard />
                  </div>
                  <MasteryTrackPanel masteryTrackPanelRef={masteryMobileRef} />

                  {isCertificateClaimingApplicable({ countryCode }) ? (
                    <>
                      <div className="flex justify-center items-center gap-4 w-full h-54">
                        <CertificateAndTestPrep
                          countryCode={countryCode as unknown as CurriculumCountryCodes}
                          currentGrade={data?.grade ?? 0}
                        />
                        {!showTestPrep && <DashboardStreakCard />}
                      </div>
                      {showTestPrep && (
                        <div className="h-46">
                          <DashboardStreakCard />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="h-46">
                      <DashboardStreakCard />
                    </div>
                  )}

                  {showDashboardStats && (
                    <div>
                      <WeeklyStats />
                    </div>
                  )}
                  <div className="flex justify-center items-center h-56 md:h-68">
                    <WeeklyChart />
                  </div>
                  <div className="flex justify-center md:items-center bg-purple-cosmos border border-white-veil rounded-4 h-80 md:h-91">
                    <LeagueContainer />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center gap-4 mx-auto min-h-screen">
              <SubscriptionModalV1 inline isTrial={isSubscriptionRequired} />
              <p className="text-white">
                {t('registerAccount:haveUser')}
                <Link className="ml-1 hover:underline" to={ROUTES.LOGIN}>
                  {t('registerAccount:logIn')}
                </Link>
              </p>
            </div>
          )}
        </div>
      </DefaultLayout>
    </>
  )
}
